<script setup>
import { onMounted, ref, nextTick, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Card from 'primevue/card';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    sitesByCountry: {
        type: Array,
        default: () => []
    },
    sitesByCountrySvg: {
        type: Array,
        default: () => []
    }
});

const isAdminContext = window.location.pathname.startsWith('/admin');

const { showLoading, hideLoading } = useLoading();

// Refs for map containers
const mapInitialized = ref(false);
const googleChartsLoaded = ref(false);
const svgMapError = ref(false);

// Load external libraries
const loadGoogleCharts = () => {
    if (!props.sitesByCountry || props.sitesByCountry.length === 0) {
        return;
    }

    // Load Google Charts library
    const script = document.createElement('script');
    script.src = 'https://www.gstatic.com/charts/loader.js';
    script.async = true;
    script.onload = () => {
        google.charts.load('current', {
            'packages': ['geochart'],
        });
        google.charts.setOnLoadCallback(drawGoogleChart);
    };
    script.onerror = () => {
        console.error('Failed to load Google Charts');
        googleChartsLoaded.value = false;
    };
    document.head.appendChild(script);
};

// Draw Google Chart
const drawGoogleChart = () => {
    if (!props.sitesByCountry || props.sitesByCountry.length === 0) {
        return;
    }

    try {
        const data = google.visualization.arrayToDataTable(props.sitesByCountry);

        const options = {
            backgroundColor: 'transparent',
            colorAxis: { colors: ['#e8f0fe', '#1976d2'] },
            datalessRegionColor: '#f5f5f5',
            defaultColor: '#f5f5f5',
            width: '100%',
            height: 500,
            keepAspectRatio: true,
            legend: {
                textStyle: {
                    color: '#495057',
                    fontSize: 12
                }
            },
            tooltip: {
                textStyle: {
                    color: '#495057',
                    fontSize: 12
                },
                trigger: 'focus'
            }
        };

        const chart = new google.visualization.GeoChart(document.getElementById('map_div'));

        // Add click event listener for country selection
        google.visualization.events.addListener(chart, 'select', () => {
            const selection = chart.getSelection();
            if (selection.length > 0) {
                const selectedRow = selection[0].row + 1; // Skip header row
                const countryId = props.sitesByCountry[selectedRow][2]; // ID is at index 2

                // Show loading before navigation
                showLoading($t('Loading sites...'));

                const sitesRoute = isAdminContext
                    ? (proxy.route ? proxy.route('sites.index') : '/admin/sites')
                    : (proxy.route ? proxy.route('client.sites.index') : '/sites');

                // Navigate to Sites page with country filter
                router.visit(sitesRoute, {
                    data: {
                        filter: {
                            url: '',
                            da: { from: 0, to: '' },
                            dr: { from: 0, to: '' },
                            gambling: false,
                            sponsor: false,
                            new: false,
                            country_id: [countryId],
                            category_id: '',
                            seller_id: '',
                            types: ''
                        },
                        ratios: {
                            BRL: 1,
                            EUR: 1,
                            USD: 1,
                            GBP: 1
                        },
                        sort: 'url'
                    },
                    onFinish: () => hideLoading(),
                    onError: () => hideLoading()
                });
            }
        });

        chart.draw(data, options);

        window.addEventListener('resize', () => {
            chart.draw(data, options);
        });

        mapInitialized.value = true;
        googleChartsLoaded.value = true;
    } catch (error) {
        console.error('Error drawing Google Chart:', error);
        googleChartsLoaded.value = false;
    }
};

// Simple world map component (fallback for svgMap issues)
const createSimpleWorldMap = () => {
    // For now, we'll just show the data in a different format
    // You could implement a simple SVG world map here or use a different library
    svgMapError.value = true;
};

// Initialize maps on mount
onMounted(() => {
    nextTick(() => {
        // Load Google Charts
        loadGoogleCharts();

        // For SVG map, we'll use the simple fallback for now
        // since svgmap has module issues
        if (props.sitesByCountrySvg && props.sitesByCountrySvg.length > 0) {
            createSimpleWorldMap();
        }
    });
});

// Handle country selection from table
const navigateToSitesWithCountry = (countryCode, countryId) => {
    // Show loading before navigation
    showLoading($t('Loading sites...'));

    const sitesRoute = isAdminContext
        ? (proxy.route ? proxy.route('sites.index') : '/admin/sites')
        : (proxy.route ? proxy.route('client.sites.index') : '/sites');

    router.visit(sitesRoute, {
        data: {
            filter: {
                url: '',
                da: { from: 0, to: '' },
                dr: { from: 0, to: '' },
                gambling: false,
                sponsor: false,
                new: false,
                country_id: [countryId],
                category_id: '',
                seller_id: '',
                types: ''
            },
            ratios: {
                BRL: 1,
                EUR: 1,
                USD: 1,
                GBP: 1
            },
            sort: 'url'
        },
        onFinish: () => hideLoading(),
        onError: () => hideLoading()
    });
};

// Watch for prop changes
</script>

<template>
    <div class="analytics-container">
        <Card>
<!--            <template #title>-->
<!--                <div class="flex align-items-center gap-2">-->
<!--                    <i class="pi pi-chart-line text-2xl"></i>-->
<!--                    <span class="text-xl font-semibold">{{ $t('Analytics') }}</span>-->
<!--                </div>-->
<!--            </template>-->

            <template #content>
                <div class="grid gap-6">
                    <!-- Sites by Country Section -->
                    <div class="col-12">
                        <div class="mb-4">
                            <h3 class="text-lg font-semibold text-gray-800">
                                <i class="pi pi-globe mr-2"></i>
                                {{ $t('Sites by country') }}
                            </h3>
                        </div>

                        <!-- Google GeoChart -->
                        <div v-if="sitesByCountry && sitesByCountry.length > 0" class="mb-6">
                            <Card>
                                <template #title>
<!--                                    <span class="text-base">{{ $t('Geographic Distribution') }}</span>-->
                                </template>
                                <template #content>
                                    <div class="flex justify-content-center">
                                        <div id="map_div" class="w-full" style="min-height: 500px;"></div>
                                    </div>
                                </template>
                            </Card>
                        </div>

                        <!-- Country Statistics Table (Alternative to SVG Map) -->
                        <div v-if="sitesByCountrySvg && sitesByCountrySvg.length > 0 && svgMapError">
                            <Card class="shadow-sm">
                                <template #content>
                                    <div class="overflow-x-auto">
                                        <table class="w-full">
                                            <thead>
                                                <tr class="border-bottom-1 surface-border">
                                                    <th class="text-left py-3 px-4">{{ $t('Country') }}</th>
                                                    <th class="text-right py-3 px-4">{{ $t('Number of Sites') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(country, index) in sitesByCountrySvg.slice(0, 3)"
                                                    :key="index"
                                                    @click="navigateToSitesWithCountry(country[0], country[2])"
                                                    class="border-bottom-1 surface-border hover:surface-hover transition-colors cursor-pointer">
                                                    <td class="py-2 px-4">
                                                        <span class="font-semibold">{{ country[0] }}</span>
                                                    </td>
                                                    <td class="text-right py-2 px-4">
                                                        <span class="badge bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                            {{ country[1] }}
                                                        </span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div v-if="sitesByCountrySvg.length > 10" class="text-center mt-3 text-gray-600">
                                            {{ $t('And') }} {{ sitesByCountrySvg.length - 3 }} {{ $t('more countries...') }}
                                        </div>
                                    </div>
                                </template>
                            </Card>
                        </div>

                        <!-- No Data Message -->
                        <div v-if="(!sitesByCountry || sitesByCountry.length === 0) && (!sitesByCountrySvg || sitesByCountrySvg.length === 0)"
                             class="text-center py-6">
                            <i class="pi pi-map text-5xl text-gray-400 mb-3"></i>
                            <p class="text-gray-600">{{ $t('No geographic data available') }}</p>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<style scoped>
.analytics-container {
    margin-top: 2rem;
}

.badge {
    display: inline-block;
    font-weight: 600;
    font-size: 0.875rem;
}

:deep(#map_div) svg path {
    cursor: pointer;
}

@media (max-width: 768px) {
    :deep(#map_div) {
        width: 80vw;
        min-height: 500px;
        margin: 0 auto;
    }
}
</style>
